import { giphyDarkGrey } from '@giphy/js-brand'
import { Button } from '@giphy/react-giphy-brand'
import hexToRgba from 'hex-rgba'
import React, { useContext, useState } from 'react'
import { IChannel } from 'shared/types/channel'
import styled from 'styled-components'
import { CollectionContext } from '..'
import AddToButton from './add-to-button'
import Breadcrumbs from './breadcumbs'
import ChannelList from './channel-list'
import CollectionDnD from './collection-dnd'
import { AddToCollectionContext } from './context'
import EmptyCollectionCTA from './empty-collection-cta'

export const Section = styled.div`
    flex: 0.5;
    overflow-y: auto;
    max-height: 300px;
    font-family: 'interface', Helvetica Neue, helvetica, sans-serif;
    font-size: 15px;
`

const Nav = styled.div`
    display: flex;
    margin: 0 0 30px;
    min-height: 300px;
    position: relative;
    ${Section}:${`nth-child(odd)`} {
        background: ${giphyDarkGrey};
    }
    ${Section}:${`nth-child(even)`} {
        background: ${hexToRgba(giphyDarkGrey, 70)};
    }
    ${Section} > :last-child {
        margin-bottom: 50px;
    }
`

const AddToButtonContainer = styled.div`
    width: 100%;
    position: absolute;
    bottom: 10px;
    display: flex;
    justify-content: center;
    pointer-events: none;

    ${Button} {
        pointer-events: auto;
        width: 33%;
    }
`

export function flatten(children: IChannel[]) {
    const flat: IChannel[] = [...children]
    children.forEach(channel => {
        if (channel.children) {
            flat.push(...flatten(channel.children))
        }
    })
    return flat
}

const empty = {} as IChannel
type Props = {
    onAdd: (channelId: number, searchChannelId: number) => void
}

const AddToCollection = ({ onAdd }: Props) => {
    const { channels, rootChannelId } = useContext(CollectionContext)
    const [isReordering, setReordering] = useState(false)
    const rootChannel = channels[rootChannelId]
    const hasChildren = rootChannel.children?.length > 0
    let [firstChannel = {} as IChannel] = rootChannel.children || [] // avoid null ref
    firstChannel = channels[firstChannel.id] || {} // don't use children for values, use the canonical channels from context
    const [activeChannelId, setActiveChannelId] = useState<number>(firstChannel.id)
    const findChannel = (id: number) => channels[id] || empty
    const selectedChannel = findChannel(activeChannelId)
    const channelSiblings = findChannel(selectedChannel.parent)
    const parentChannel = findChannel(selectedChannel.parent)
    const value = {
        activeChannelId,
        setActiveChannelId,
        isReordering,
    }
    const showBreadcrumbs = hasChildren && selectedChannel && selectedChannel.parent !== rootChannelId
    return (
        <AddToCollectionContext.Provider value={value}>
            {showBreadcrumbs && <Breadcrumbs channel={parentChannel} />}
            {hasChildren ? (
                <Nav>
                    <CollectionDnD setReordering={setReordering}>
                        <Section key={parentChannel.id}>
                            <ChannelList channel={channelSiblings} key={channelSiblings.id} />
                        </Section>
                        <Section>
                            <ChannelList channel={selectedChannel} key={selectedChannel.id} />
                        </Section>
                    </CollectionDnD>
                    <AddToButtonContainer>
                        <AddToButton
                            displayName={selectedChannel.display_name}
                            onClick={() => onAdd(activeChannelId, rootChannelId)}
                        />
                    </AddToButtonContainer>
                </Nav>
            ) : (
                <EmptyCollectionCTA channel={rootChannel} />
            )}
        </AddToCollectionContext.Provider>
    )
}
export default AddToCollection
